import { useCallback, useState, useEffect, useRef } from 'react'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
} from '@/utils/tour-styles'
import { UseFormReturn, UseFieldArrayAppend } from 'react-hook-form'
import { Contract } from '@/modules/Contract/types'
import { initServiceTimes } from '@/modules/Contract/constants/function'
import dayjs from 'dayjs'

interface TourStep {
    selector: string
    title: string
    description: string
}

interface TourOption {
    id: string
    name: string
    description: string
    productGroup?: string // Product group to auto-add
    dynamicConfig?: boolean // If true, inject config steps from service_configoptions
    steps: TourStep[]
}

// Mapping from product_group_name (display) to tour productGroup
const PRODUCT_GROUP_MAPPING: Record<string, string> = {
    // Server variants
    'BARE METAL SERVER/ DEDICATED SERVER': 'SERVER',
    SERVER: 'SERVER',
    COLOCATION: 'SERVER',
    // CDN / SwiftFederation variants
    SWIFT_FEDERATION: 'SWIFT_FEDERATION',
    SWIFTFEDERATION: 'SWIFT_FEDERATION',
    CDN: 'SWIFT_FEDERATION',
    // Cloud variants
    CLOUD: 'CLOUD',
    VCLOUD: 'CLOUD',
    VPS: 'CLOUD',
    // Domain variants
    DOMAIN: 'DOMAIN',
    TLDS: 'DOMAIN',
    VNNIC: 'DOMAIN',
    // SSL variants
    SSL: 'SSL',
    'SSL CERTIFICATE': 'SSL',
}

// Normalize product group name for matching
const normalizeProductGroup = (groupName: string | null): string | null => {
    if (!groupName) return null
    const upper = groupName.toUpperCase()
    // Direct match
    if (PRODUCT_GROUP_MAPPING[upper]) return PRODUCT_GROUP_MAPPING[upper]
    // Partial match
    for (const [key, value] of Object.entries(PRODUCT_GROUP_MAPPING)) {
        if (upper.includes(key) || key.includes(upper)) return value
    }
    return null
}

// Config type to tour steps mapping - returns array of detailed steps for each field
const CONFIG_TYPE_STEPS: Record<string, (configName: string) => TourStep[]> = {
    'server-warehouse': () => [
        {
            selector: '[data-tour="config-server"]',
            title: 'SERVER - Tổng quan',
            description:
                'Cấu hình thông tin máy chủ vật lý (Bare Metal Server)',
        },
        {
            selector: '[data-tour="server-model"]',
            title: '1. Model Server',
            description:
                'Click vào ô này để mở dropdown\nDanh sách model: Dell R740, HPE DL380, Supermicro...\nChọn model → Tự động điền CPU, nhà sản xuất',
        },
        {
            selector: '[data-tour="server-manufacture"]',
            title: '2. Nhà sản xuất (Auto)',
            description:
                'Field này tự động điền\nKhi chọn model ở bước trước → Nhà sản xuất tự động hiện\nVí dụ: Dell, HPE, Supermicro...',
        },
        {
            selector: '[data-tour="server-service-tag"]',
            title: '3. Service Tag / Serial Number',
            description:
                'Nhập mã định danh server\nVí dụ: ABCD1234, SN-2024-001\nMã này dùng để quản lý thiết bị trong hệ thống',
        },
        {
            selector: '[data-tour="server-vcores"]',
            title: '4. vCores (CPU)',
            description:
                'Số lõi CPU của server\nTự động điền theo model đã chọn\nCó thể chỉnh sửa nếu cần',
        },
    ],
    bandwidth: () => [
        {
            selector: '[data-tour="config-bandwidth"]',
            title: 'BĂNG THÔNG - Tổng quan',
            description: 'Cấu hình dung lượng và giá bandwidth cho dịch vụ.',
        },
        {
            selector: '[data-tour="bandwidth-commitment-value"]',
            title: '1. Dung lượng cam kết',
            description:
                'Nhập số lượng bandwidth cam kết\nVí dụ: 10 (= 10TB/kỳ)\nĐơn vị tính thường là TB hoặc GB',
        },
        {
            selector: '[data-tour="bandwidth-price-per-unit"]',
            title: '2. Đơn giá',
            description:
                'Nhập giá tiền cho mỗi đơn vị\nVí dụ: 500000 (= 500,000 VND/TB)\nGiá này áp dụng cho phần cam kết',
        },
        {
            selector: '[data-tour="bandwidth-price-total"]',
            title: '3. Thành tiền (Auto)',
            description:
                'Tự động tính\nCông thức: Dung lượng  Đơn giá\nĐây là phí cố định mỗi kỳ thanh toán',
        },
        {
            selector: '[data-tour="bandwidth-prepaid"]',
            title: '4. Trả trước',
            description:
                'Tick = Khách trả trước toàn bộ cam kết\nKhông tick = Trả theo kỳ (tháng/quý/năm)',
        },
        {
            selector: '[data-tour="bandwidth-commitment-time"]',
            title: '5. Thời gian cam kết',
            description:
                'Nhập số kỳ cam kết:\n• = 1: Reset mỗi tháng, đối soát theo tháng\n• > 1: Cam kết dồn, dùng hết mới tính vượt\nVí dụ: 12 = cam kết cả năm',
        },
        {
            selector: '[data-tour="bandwidth-price-method"]',
            title: '6. Hình thức đối soát',
            description:
                'Click để chọn hình thức:\n• Lũy tiến: Dùng càng nhiều giá càng rẻ\n• Bậc thang: Tính theo từng mức riêng\n• Cố định: 1 giá duy nhất',
        },
    ],
    transcoding: () => [
        {
            selector: '[data-tour="config-bandwidth"]',
            title: 'TRANSCODING - Tổng quan',
            description: 'Cấu hình dịch vụ chuyển đổi video/stream.',
        },
        {
            selector: '[data-tour="bandwidth-commitment-value"]',
            title: '1. Số giờ cam kết',
            description: 'Nhập số giờ transcoding cam kết mỗi kỳ.',
        },
        {
            selector: '[data-tour="bandwidth-price-per-unit"]',
            title: '2. Đơn giá / giờ',
            description: 'Giá tiền cho mỗi giờ transcoding.',
        },
    ],
    warehouse: (configName: string) => {
        const name = configName?.toLowerCase() || 'warehouse'
        if (name.includes('disk') || name.includes('storage')) {
            return [
                {
                    selector: '[data-tour="config-disk"]',
                    title: 'STORAGE - Tổng quan',
                    description:
                        'Cấu hình ổ cứng lưu trữ cho server.\nClick nút + để thêm loại ổ khác',
                },
                {
                    selector: '[data-tour="warehouse-model-disk"]',
                    title: '1. Model ổ cứng',
                    description:
                        'Click để mở dropdown chọn model\nDanh sách:\n• SSD: Samsung 870 EVO, Intel D3-S4510...\n• HDD: Seagate Exos, WD Gold...\nChọn model → Auto điền dung lượng, nhà SX',
                },
                {
                    selector: '[data-tour="warehouse-manufacture-disk"]',
                    title: '2. Nhà sản xuất (Auto)',
                    description:
                        'Field này tự động điền\nKhi chọn model → Nhà SX tự động hiện\nVí dụ: Samsung, Intel, Seagate, WD...',
                },
                {
                    selector: '[data-tour="warehouse-quantity-disk"]',
                    title: '3. Số lượng',
                    description:
                        'Click để chọn số lượng ổ cần mua\nGiới hạn theo quota (nếu có)\nDropdown tự động tính số lượng tối đa cho phép',
                },
                {
                    selector: '[data-tour="warehouse-capacity-disk"]',
                    title: '4. Dung lượng (Auto)',
                    description:
                        'Tự động tính từ model đã chọn\nCông thức: Số lượng  Dung lượng/ổ\nĐơn vị: GB hoặc TB',
                },
            ]
        }
        if (name.includes('memory') || name.includes('ram')) {
            return [
                {
                    selector: '[data-tour="config-memory"]',
                    title: 'RAM - Tổng quan',
                    description:
                        'Cấu hình bộ nhớ RAM cho server.\nClick nút + để thêm loại RAM khác',
                },
                {
                    selector: '[data-tour="warehouse-model-memory"]',
                    title: '1. Model RAM',
                    description:
                        'Click để mở dropdown chọn model\nDanh sách:\n• DDR4: 8GB, 16GB, 32GB, 64GB\n• DDR5: 16GB, 32GB, 64GB, 128GB\nChọn model → Auto điền dung lượng, nhà SX',
                },
                {
                    selector: '[data-tour="warehouse-manufacture-memory"]',
                    title: '2. Nhà sản xuất (Auto)',
                    description:
                        'Field này tự động điền\nKhi chọn model → Nhà SX tự động hiện\nVí dụ: Samsung, SK Hynix, Micron...',
                },
                {
                    selector: '[data-tour="warehouse-quantity-memory"]',
                    title: '3. Số lượng',
                    description:
                        'Click để chọn số thanh RAM cần mua\nGiới hạn theo quota (nếu có)\nDropdown tự động tính số lượng tối đa cho phép',
                },
                {
                    selector: '[data-tour="warehouse-capacity-memory"]',
                    title: '4. Dung lượng (Auto)',
                    description:
                        'Tự động tính từ model đã chọn\nCông thức: Số lượng  Dung lượng/thanh\nĐơn vị: GB',
                },
            ]
        }
        return [
            {
                selector: `[data-tour="config-${name}"]`,
                title: `${configName?.toUpperCase() || 'WAREHOUSE'}`,
                description: 'Cấu hình thiết bị/vật tư.',
            },
        ]
    },
    'base-config': (configName: string) => [
        {
            selector: `[data-tour="config-${configName?.toLowerCase() || 'base'}"]`,
            title: `${configName?.toUpperCase() || 'CONFIG'}`,
            description: 'Cấu hình cơ bản cho dịch vụ.',
        },
    ],
}

// Generate dynamic config steps from service_configoptions
const generateConfigSteps = (
    configOptions: Array<{ type: string; name: string }> | null
): TourStep[] => {
    if (!configOptions || configOptions.length === 0) return []

    const steps: TourStep[] = []
    const seenSelectors = new Set<string>()

    for (const config of configOptions) {
        const stepGenerator = CONFIG_TYPE_STEPS[config.type]
        if (stepGenerator) {
            const generatedSteps = stepGenerator(config.name)
            for (const step of generatedSteps) {
                if (!seenSelectors.has(step.selector)) {
                    steps.push(step)
                    seenSelectors.add(step.selector)
                }
            }
        }
    }

    return steps
}

// Tour options for Contract Create form
const TOUR_OPTIONS: TourOption[] = [
    {
        id: 'overview',
        name: 'Tour tổng quan',
        description: 'Hướng dẫn tổng quan về form tạo hợp đồng',
        steps: [
            {
                selector: '[data-tour="contract-form"]',
                title: 'Form tạo hợp đồng',
                description:
                    'Đây là form tạo hợp đồng mới cho khách hàng. Bao gồm thông tin khách hàng, thời hạn hợp đồng, và các sản phẩm/dịch vụ.',
            },
            {
                selector: '[data-tour="contract-customer"]',
                title: 'Chọn khách hàng *',
                description:
                    'Tìm và chọn khách hàng sẽ ký hợp đồng. Bạn có thể tìm theo tên, email hoặc mã khách hàng.',
            },
            {
                selector: '[data-tour="contract-contact"]',
                title: 'Người liên hệ',
                description:
                    'Chọn người liên hệ của khách hàng sẽ đại diện ký hợp đồng.',
            },
            {
                selector: '#contract-effective-date',
                title: 'Ngày hiệu lực *',
                description:
                    'Chọn ngày hợp đồng bắt đầu có hiệu lực. Mặc định là ngày hôm nay.',
            },
            {
                selector: '#contract-expiry-date',
                title: 'Ngày hết hạn *',
                description:
                    'Chọn ngày hợp đồng hết hiệu lực. Mặc định là 1 năm sau ngày hiệu lực.',
            },
            {
                selector: '#contract-currency',
                title: 'Đơn vị tiền tệ',
                description:
                    'Chọn đơn vị tiền tệ cho hợp đồng: VND, USD, EUR, hoặc JPY.',
            },
            {
                selector: '#add-product-button',
                title: 'Thêm sản phẩm/dịch vụ',
                description:
                    'Click để thêm sản phẩm hoặc dịch vụ vào hợp đồng. Có thể thêm nhiều sản phẩm.',
            },
            {
                selector: '[data-tour="contract-products"]',
                title: 'Danh sách sản phẩm',
                description:
                    'Khu vực hiển thị các sản phẩm/dịch vụ đã thêm vào hợp đồng. Mỗi sản phẩm có thể cấu hình riêng.',
            },
            {
                selector: '[data-tour="pricing-model"]',
                title: 'Mô hình tính giá',
                description:
                    'Click để chọn mô hình tính giá:\n• Giá trọn gói (Package): 1 giá cố định cho cả sản phẩm\n• Giá từng item (Bundle): Tổng = Σ (số lượng  đơn giá) của từng config\n• % dịch vụ chính: Addon tính theo % giá dịch vụ parent',
            },
            {
                selector: '[data-tour="contract-total"]',
                title: 'Tổng giá trị hợp đồng',
                description:
                    'Hiển thị tổng giá trị của tất cả sản phẩm/dịch vụ trong hợp đồng.',
            },
            {
                selector: '#save-contract-button',
                title: 'Lưu hợp đồng',
                description:
                    'Nhấn nút để tạo hợp đồng mới. Đảm bảo đã chọn khách hàng và thêm ít nhất 1 sản phẩm.',
            },
        ],
    },
    {
        id: 'sla',
        name: 'Cấu hình SLA',
        description: 'Hướng dẫn cấu hình SLA cho hợp đồng',
        steps: [
            {
                selector: '[data-tour="contract-form"]',
                title: 'Cấu hình SLA',
                description:
                    'SLA (Service Level Agreement) định nghĩa cam kết chất lượng dịch vụ giữa bạn và khách hàng.',
            },
            {
                selector: '[name="sla_enable"]',
                title: '1. Kích hoạt SLA',
                description:
                    'Tick vào checkbox này để bật tính năng SLA cho hợp đồng.\nKhi bật, form cấu hình SLA sẽ hiển thị bên dưới.',
            },
            {
                selector: '[data-tour="sla-config-section"]',
                title: '2. Phần cấu hình SLA',
                description:
                    'Khu vực cấu hình SLA xuất hiện khi bạn tick vào SLA Enable.\nBao gồm: Nguồn SLA, Chỉ số cam kết, Giờ làm việc.',
            },
            {
                selector: '[data-tour="sla-source-selector"]',
                title: '3. Chọn nguồn SLA',
                description:
                    '3 cách cấu hình SLA:\n• Từ template: Chọn template có sẵn (Bronze, Silver, Gold, Platinum)\n• Tùy chỉnh: Nhập thủ công các chỉ số\n• Mặc định: SLA tự động theo thời hạn hợp đồng',
            },
            {
                selector: '[data-tour="sla-metrics"]',
                title: '4. Chỉ số SLA',
                description:
                    '• Uptime (%): Tỷ lệ thời gian dịch vụ hoạt động (VD: 99.9%)\n• Downtime cho phép: Số phút downtime tối đa/tháng\n• Response time: Thời gian phản hồi ticket\n• Resolution time: Thời gian xử lý ticket',
            },
            {
                selector: '[data-tour="sla-business-hours"]',
                title: '5. Giờ làm việc',
                description:
                    'Cấu hình khung giờ tính SLA:\n• Tick "Chỉ tính giờ làm việc": SLA chỉ tính trong giờ hành chính\n• Múi giờ: Asia/Ho_Chi_Minh (UTC+7)\n• Ngày làm việc: T2-T6 (có thể thêm T7, CN)',
            },
            {
                selector: '#save-contract-button',
                title: '6. Lưu hợp đồng với SLA',
                description:
                    'Sau khi cấu hình xong SLA, nhấn Lưu để tạo hợp đồng.\nHệ thống sẽ tự động tạo Service SLA khi tạo dịch vụ.',
            },
        ],
    },
    {
        id: 'cdn',
        name: 'Sản phẩm CDN',
        description: 'Hướng dẫn cấu hình sản phẩm SwiftFederation CDN',
        productGroup: 'SWIFT_FEDERATION',
        steps: [
            {
                selector: '[data-tour="select-product-group"]',
                title: '1. Chọn nhóm sản phẩm',
                description:
                    'Chọn nhóm sản phẩm "SWIFT_FEDERATION" từ dropdown này.',
            },
            {
                selector: '#select-product',
                title: '2. Chọn sản phẩm CDN',
                description:
                    'Sau khi chọn nhóm, chọn sản phẩm "CDN" từ danh sách.',
            },
            {
                selector: '.product-form-select',
                title: '3. Hình thức dịch vụ',
                description:
                    'Chọn hình thức:\n• Cam kết (Commitment): Có dung lượng cam kết\n• Trả theo dùng (Pay as you go): Không cam kết',
            },
            {
                selector: '[data-tour="pricing-model"]',
                title: '4. Mô hình tính giá',
                description:
                    'Chọn mô hình:\n• Giá trọn gói (Package): 1 giá cố định\n• Giá từng item (Bundle): Tính tổng từ config',
            },
            {
                selector: '[data-tour="config-bandwidth"]',
                title: '5. Cấu hình Bandwidth',
                description:
                    'Khu vực cấu hình bandwidth cho từng region.\nMỗi region có thể có cấu hình riêng.',
            },
            {
                selector: '[data-tour="bandwidth-commitment-value"]',
                title: '5.1 Dung lượng cam kết',
                description:
                    'Nhập số lượng bandwidth cam kết\nVí dụ: 1000 (= 1000 TB/kỳ)\nĐơn vị tính thường là TB',
            },
            {
                selector: '[data-tour="bandwidth-price-per-unit"]',
                title: '5.2 Đơn giá bán',
                description:
                    'Giá tiền cho mỗi đơn vị cam kết\nVí dụ: 500000 (= 500,000 VND/TB)\nGiá này áp dụng cho phần cam kết',
            },
            {
                selector: '[data-tour="bandwidth-price-total"]',
                title: '5.3 Tổng giá bán (Auto)',
                description:
                    'Tự động tính\nCông thức: Dung lượng  Đơn giá\nĐây là phí cố định mỗi kỳ thanh toán',
            },
            {
                selector: '[data-tour="bandwidth-prepaid"]',
                title: '5.4 Trả trước / Trả sau',
                description:
                    ' Tick = Khách trả trước toàn bộ cam kết\n✗ Không tick = Trả sau theo kỳ (tháng/quý/năm)',
            },
            {
                selector: '[data-tour="bandwidth-commitment-time"]',
                title: '5.5 Thời gian cam kết',
                description:
                    'Nhập số kỳ cam kết:\n• = 1: Reset mỗi tháng, đối soát theo tháng\n• > 1: Cam kết dồn, dùng hết mới tính vượt\nVí dụ: 12 = cam kết cả năm',
            },
            {
                selector: '[data-tour="bandwidth-price-method"]',
                title: '5.6 Loại đối soát',
                description:
                    'Chọn hình thức đối soát vượt cam kết:\n• Lũy tiến: Dùng càng nhiều giá càng rẻ\n• Bậc thang: Tính theo từng mức riêng\n• Theo thỏa thuận: Giá cố định cho vượt',
            },
            {
                selector: '[data-tour="bandwidth-overage-pricing"]',
                title: '5.7 Cấu hình đơn giá vượt',
                description:
                    'Bảng giá cho phần vượt cam kết:\n• Đơn giá/TB theo từng mức\n• Mức từ 0-5TB: giá X\n• Mức 5-10TB: giá Y (thấp hơn)\n• ...',
            },
            {
                selector: '[data-tour="cdn-merge"]',
                title: '6. Gộp bandwidth',
                description:
                    'Click để gộp tất cả bandwidth thành 1 global.\nHữu ích khi muốn tính chung cho tất cả regions.',
            },
            {
                selector: '[data-tour="cdn-add-region"]',
                title: '7. Thêm region',
                description:
                    'Click để thêm region mới.\nMỗi region có thể có cấu hình bandwidth riêng.',
            },
            {
                selector: '[data-tour="service-times"]',
                title: '8. Thời gian dịch vụ',
                description:
                    'Cấu hình thời gian thanh toán, ngày bắt đầu/kết thúc cho từng kỳ.',
            },
        ],
    },
    {
        id: 'vcloud',
        name: 'Sản phẩm VCloud',
        description: 'Hướng dẫn cấu hình sản phẩm Cloud VCloud (VM)',
        productGroup: 'CLOUD',
        steps: [
            {
                selector: '[data-tour="select-product-group"]',
                title: '1. Chọn nhóm sản phẩm',
                description: 'Chọn nhóm sản phẩm "CLOUD" từ dropdown này.',
            },
            {
                selector: '#select-product',
                title: '2. Chọn sản phẩm VCloud',
                description:
                    'Sau khi chọn nhóm, chọn sản phẩm "VCloud" từ danh sách.',
            },
            {
                selector: '[data-tour="pricing-model"]',
                title: 'Mô hình tính giá',
                description:
                    'Chọn mô hình tính giá:\n• Giá trọn gói (Package): Mỗi VM có giá cố định\n• Giá từng item (Bundle): Tính theo vCPU + RAM + Storage riêng lẻ',
            },
            {
                selector: '[data-tour="vcloud-package-type"]',
                title: 'Loại cấu hình VM',
                description:
                    'Chọn "Gói định sẵn" (predefined) hoặc "Tùy chỉnh quota" (custom) cho VM instances.',
            },
            {
                selector: '[data-tour="vcloud-instances"]',
                title: 'Danh sách VM Instances',
                description:
                    'Thêm và cấu hình các VM instances. Mỗi instance có thể chọn package và số lượng.',
            },
            {
                selector: '[data-tour="vcloud-add-vm"]',
                title: 'Thêm VM Instance',
                description:
                    'Click để thêm VM instance mới. Có thể thêm nhiều loại VM khác nhau.',
            },
            {
                selector: '[data-tour="vcloud-prepaid"]',
                title: 'Hình thức thanh toán',
                description: 'Chọn trả trước hoặc trả sau cho dịch vụ VCloud.',
            },
            {
                selector: '[data-tour="service-times"]',
                title: 'Thời gian dịch vụ',
                description:
                    'Cấu hình thời gian thanh toán cho dịch vụ VCloud.',
            },
        ],
    },
    {
        id: 'domain',
        name: 'Sản phẩm Domain',
        description: 'Hướng dẫn cấu hình sản phẩm tên miền (TLDs/VNNIC)',
        productGroup: 'DOMAIN',
        steps: [
            {
                selector: '[data-tour="select-product-group"]',
                title: '1. Chọn nhóm sản phẩm',
                description: 'Chọn nhóm sản phẩm "DOMAIN" từ dropdown này.',
            },
            {
                selector: '#select-product',
                title: '2. Chọn loại tên miền',
                description:
                    'Chọn loại: "TLDs" (quốc tế) hoặc "VNNIC" (Việt Nam).',
            },
            {
                selector: '[data-tour="domain-type"]',
                title: '3. Loại đăng ký',
                description:
                    'Chọn hình thức:\n• Đăng ký mới: Domain chưa được đăng ký\n• Đổi nhà cung cấp: Chuyển domain từ nhà đăng ký khác',
            },
            {
                selector: '[data-tour="domain-name"]',
                title: '4. Tên miền',
                description:
                    'Nhập tên miền cần đăng ký\nVí dụ: example.com, mywebsite.vn\nHệ thống tự động tính giá theo đuôi domain',
            },
            {
                selector: '[data-tour="domain-period"]',
                title: '5. Thời hạn đăng ký',
                description:
                    'Chọn thời hạn đăng ký:\n• 1 năm, 2 năm, 3 năm...\nThời hạn này sẽ sync với thời hạn hợp đồng',
            },
            {
                selector: '[data-tour="domain-price"]',
                title: '6. Đơn giá bán/năm',
                description:
                    'Giá bán cho khách hàng\nTự động tính: Đơn giá  Thời hạn\nCó thể chỉnh sửa thủ công nếu cần',
            },
            {
                selector: '[data-tour="service-times"]',
                title: 'Thời gian dịch vụ',
                description:
                    'Thời gian đăng ký tên miền được sync với thời hạn hợp đồng.',
            },
        ],
    },
    {
        id: 'vnnic',
        name: 'Domain VNNIC',
        description: 'Hướng dẫn cấu hình tên miền Việt Nam (.vn)',
        productGroup: 'DOMAIN',
        steps: [
            {
                selector: '[data-tour="select-product-group"]',
                title: '1. Chọn nhóm sản phẩm',
                description: 'Chọn nhóm sản phẩm "DOMAIN" từ dropdown này.',
            },
            {
                selector: '#select-product',
                title: '2. Chọn VNNIC',
                description:
                    'Chọn loại "VNNIC" cho tên miền Việt Nam (.vn, .com.vn, .net.vn...).',
            },
            {
                selector: '[data-tour="domain-type"]',
                title: '3. Loại đăng ký',
                description:
                    'Chọn hình thức:\n• Đăng ký mới: Domain chưa được đăng ký\n• Đổi nhà cung cấp: Chuyển domain từ nhà đăng ký khác',
            },
            {
                selector: '[data-tour="domain-name"]',
                title: '4. Tên miền',
                description:
                    'Nhập tên miền cần đăng ký\nVí dụ: example.vn, mysite.com.vn',
            },
            {
                selector: '[data-tour="domain-period"]',
                title: '5. Thời hạn đăng ký',
                description:
                    'Chọn thời hạn: 1 năm, 2 năm, 3 năm...\nThời hạn sync với hợp đồng',
            },
            {
                selector: '[data-tour="domain-price"]',
                title: '6. Đơn giá bán/năm',
                description:
                    'Giá bán cho khách hàng\nCó thể chỉnh sửa thủ công',
            },
            {
                selector: '[data-tour="vnnic-steps"]',
                title: '7. Thông tin VNNIC (4 bước)',
                description:
                    'VNNIC yêu cầu điền đầy đủ 4 bước thông tin.\nClick vào từng tab để điền thông tin.',
            },
            {
                selector: '[data-tour="vnnic-customer"]',
                title: '7.1 Thông tin khách hàng',
                description:
                    'Điền thông tin chủ sở hữu domain:\n• Tên KH (cá nhân/công ty)\n• CMND/MST\n• Địa chỉ, Email, SĐT',
            },
            {
                selector: '[data-tour="vnnic-declares"]',
                title: '7.2 Bản khai tên miền',
                description:
                    'Thông tin người đứng tên bản khai:\n• Loại: Cá nhân / Doanh nghiệp\n• Họ tên, CMND/MST\n• Địa chỉ, Email, SĐT\n• Chức vụ, Công ty',
            },
            {
                selector: '[data-tour="vnnic-admin"]',
                title: '7.3 Người quản lý (Admin)',
                description:
                    'Người quản lý kỹ thuật domain:\n• Họ tên, CMND\n• Địa chỉ, Email, SĐT\n• Chức vụ, Công ty\nNgười này sẽ nhận thông báo từ VNNIC',
            },
            {
                selector: '[data-tour="vnnic-settings"]',
                title: '7.4 Cài đặt thêm',
                description:
                    'Tùy chọn bổ sung:\n• Gửi email thông báo cho khách hàng',
            },
            {
                selector: '[data-tour="service-times"]',
                title: 'Thời gian dịch vụ',
                description: 'Thời gian đăng ký sync với thời hạn hợp đồng.',
            },
        ],
    },
    {
        id: 'ssl',
        name: 'Sản phẩm SSL',
        description: 'Hướng dẫn cấu hình sản phẩm SSL Certificate',
        productGroup: 'SSL',
        steps: [
            {
                selector: '[data-tour="select-product-group"]',
                title: '1. Chọn nhóm sản phẩm',
                description: 'Chọn nhóm sản phẩm "SSL" từ dropdown này.',
            },
            {
                selector: '#select-product',
                title: '2. Chọn loại SSL',
                description:
                    'Chọn loại SSL Certificate phù hợp (DV, OV, EV, Wildcard...).',
            },
            {
                selector: '[data-tour="ssl-config"]',
                title: 'Cấu hình SSL',
                description:
                    'Nhập tên miền cần SSL, chọn thời hạn đăng ký và hình thức thanh toán.',
            },
            {
                selector: '[data-tour="service-times"]',
                title: 'Thời gian hiệu lực',
                description: 'Thời gian hiệu lực của SSL certificate.',
            },
        ],
    },
    {
        id: 'server',
        name: 'Sản phẩm Server/VBM',
        description: 'Hướng dẫn cấu hình sản phẩm Server VBM',
        productGroup: 'SERVER',
        dynamicConfig: true, // Flag to inject dynamic config steps
        steps: [
            {
                selector: '[data-tour="select-product-group"]',
                title: '1. Chọn nhóm sản phẩm',
                description: 'Chọn nhóm sản phẩm "SERVER" từ dropdown này.',
            },
            {
                selector: '#select-product',
                title: '2. Chọn loại Server',
                description:
                    'Chọn loại server: "VBM" (Bare Metal) hoặc "Colocation".',
            },
            {
                selector: '[data-tour="pricing-model"]',
                title: '3. Mô hình tính giá',
                description:
                    'Chọn mô hình tính giá cho sản phẩm:\n• Giá trọn gói (Package): 1 giá cố định\n• Giá từng item (Bundle): Tổng từ từng config',
            },
            // Dynamic config steps will be injected here based on service_configoptions
            {
                selector: '[data-tour="service-times"]',
                title: 'Thời gian thuê',
                description: 'Cấu hình thời gian thuê server và kỳ thanh toán.',
            },
        ],
    },
]

interface UseContractTourOptions {
    formControl?: UseFormReturn<Contract>
    append?: UseFieldArrayAppend<Contract, 'order.products'>
}

/**
 * Tour hook for Contract Create form with multi-tour support
 * Shows a selector modal to choose which tour to view
 * Auto-adds product template when selecting product-specific tours
 */
export const useContractTour = (options: UseContractTourOptions = {}) => {
    const { formControl, append } = options
    const [isActive, setIsActive] = useState(false)
    const [currentTourStep, setCurrentTourStep] = useState(0)
    const [showSelector, setShowSelector] = useState(false)
    const [selectedTour, setSelectedTour] = useState<TourOption | null>(null)
    const pendingTourRef = useRef<TourOption | null>(null)

    const showTourUI = (
        step: TourStep,
        stepIndex: number,
        tour: TourOption
    ) => {
        injectTourStyles()
        cleanupTourElements()
        createTourOverlay()

        const element = document.querySelector(step.selector) as HTMLElement
        if (!element) {
            // Skip to next step if element not found
            if (stepIndex < tour.steps.length - 1) {
                goToStep(stepIndex + 1, tour)
            } else {
                endTour()
            }
            return
        }

        element.classList.add('tour-highlight-element')
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })

        // Wait for scroll then add popover
        setTimeout(() => {
            const popover = createTourPopover({
                title: step.title,
                description: step.description,
                currentStep: stepIndex + 1,
                totalSteps: tour.steps.length,
                onPrev:
                    stepIndex > 0
                        ? () => goToStep(stepIndex - 1, tour)
                        : undefined,
                onNext: () => {
                    if (stepIndex === tour.steps.length - 1) {
                        endTour()
                    } else {
                        goToStep(stepIndex + 1, tour)
                    }
                },
                onClose: endTour,
                isLastStep: stepIndex === tour.steps.length - 1,
            })

            document.body.appendChild(popover)
            positionPopover(popover, element)
        }, 300)
    }

    const goToStep = (stepIndex: number, tour: TourOption) => {
        const step = tour.steps[stepIndex]
        if (!step) return

        cleanupTourElements()
        showTourUI(step, stepIndex, tour)
        setCurrentTourStep(stepIndex)
    }

    const endTour = useCallback(() => {
        cleanupTourElements()
        removeSelectorModal()
        setIsActive(false)
        setCurrentTourStep(0)
        setSelectedTour(null)
        setShowSelector(false)
    }, [])

    const removeSelectorModal = () => {
        document.getElementById('tour-selector-modal')?.remove()
        document.getElementById('tour-selector-overlay')?.remove()
    }

    // Inject dynamic config steps into tour based on service_configoptions
    const injectDynamicConfigSteps = useCallback(
        (tour: TourOption): TourOption => {
            if (!tour.dynamicConfig || !formControl) return tour

            const products = formControl.getValues('order.products') || []
            if (products.length === 0) return tour

            // Get config options from first product
            const configOptions = products[0]?.service_configoptions as
                | Array<{ type: string; name: string }>
                | undefined
            if (!configOptions || configOptions.length === 0) return tour

            // Generate dynamic steps
            const dynamicSteps = generateConfigSteps(configOptions)
            if (dynamicSteps.length === 0) return tour

            // Find service-times step index (inject before it)
            const serviceTimesIndex = tour.steps.findIndex((s) =>
                s.selector.includes('service-times')
            )

            // Build new steps array with dynamic steps injected
            const newSteps = [...tour.steps]
            if (serviceTimesIndex !== -1) {
                newSteps.splice(serviceTimesIndex, 0, ...dynamicSteps)
            } else {
                // No service-times step found, append at end
                newSteps.push(...dynamicSteps)
            }

            return { ...tour, steps: newSteps }
        },
        [formControl]
    )

    const startSelectedTour = useCallback(
        (tour: TourOption) => {
            removeSelectorModal()

            // Check if tour requires a specific product and no products exist yet
            if (tour.productGroup && formControl && append) {
                const products = formControl.getValues('order.products') || []

                if (products.length === 0) {
                    // No products yet - auto-add product with matching product group
                    const today = dayjs()
                    append({
                        product_id: 0,
                        product_group_name: tour.productGroup,
                        product_form: 'commit',
                        service_times: initServiceTimes(today),
                        service_price_register: 0,
                        editable: true,
                        prepaid: true,
                    })

                    // Store pending tour and wait for product to render
                    pendingTourRef.current = tour
                    setShowSelector(false)
                    return
                }
                // If products exist, they already match (disabled buttons prevent mismatch)
            }

            // Inject dynamic config steps if applicable
            const finalTour = injectDynamicConfigSteps(tour)

            // Start tour immediately
            setSelectedTour(finalTour)
            setIsActive(true)
            setCurrentTourStep(0)
            setShowSelector(false)

            // Scroll to top first
            window.scrollTo({ top: 0, behavior: 'smooth' })

            // Start tour after scroll
            setTimeout(() => {
                showTourUI(finalTour.steps[0], 0, finalTour)
            }, 300)
        },
        [formControl, append, injectDynamicConfigSteps]
    )

    // Get current product group from form (normalized for tour matching)
    const getCurrentProductGroup = useCallback((): {
        raw: string | null
        normalized: string | null
    } => {
        if (!formControl) return { raw: null, normalized: null }
        const products = formControl.getValues('order.products') || []
        if (products.length === 0) return { raw: null, normalized: null }
        const raw = products[0]?.product_group_name || null
        return { raw, normalized: normalizeProductGroup(raw) }
    }, [formControl])

    const showTourSelector = useCallback(() => {
        const currentProductGroup = getCurrentProductGroup()

        // Create overlay
        const overlay = document.createElement('div')
        overlay.id = 'tour-selector-overlay'
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            z-index: 9998;
        `
        overlay.onclick = endTour

        // Create modal
        const modal = document.createElement('div')
        modal.id = 'tour-selector-modal'
        modal.style.cssText = `
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: white;
            border-radius: 12px;
            padding: 24px;
            width: 480px;
            max-width: calc(100vw - 40px);
            max-height: calc(100vh - 80px);
            overflow-y: auto;
            box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
            z-index: 9999;
            font-family: 'Google Sans', ui-sans-serif, system-ui, sans-serif;
        `

        modal.innerHTML = `
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
                <h2 style="margin: 0; font-size: 18px; font-weight: 600; color: #1f2937;">
                    Chọn hướng dẫn
                </h2>
                <button id="tour-selector-close" style="
                    background: none;
                    border: none;
                    font-size: 24px;
                    cursor: pointer;
                    color: #9ca3af;
                    line-height: 1;
                    padding: 4px;
                "></button>
            </div>
            <p style="margin: 0 0 16px 0; font-size: 14px; color: #6b7280;">
                ${currentProductGroup.raw ? `Sản phẩm hiện tại: <strong>${currentProductGroup.raw}</strong>` : 'Chọn loại hướng dẫn phù hợp với nhu cầu của bạn:'}
            </p>
            <div id="tour-options-list" style="display: flex; flex-direction: column; gap: 8px;">
                ${TOUR_OPTIONS.map((tour) => {
                    const normalizedCurrent = currentProductGroup.normalized
                    const isMatching =
                        !tour.productGroup ||
                        !normalizedCurrent ||
                        tour.productGroup === normalizedCurrent
                    const isRecommended =
                        tour.productGroup === normalizedCurrent

                    return `
                    <button
                        class="tour-option-btn"
                        data-tour-id="${tour.id}"
                        ${!isMatching ? 'disabled' : ''}
                        style="
                            display: flex;
                            flex-direction: column;
                            align-items: flex-start;
                            padding: 12px 16px;
                            border: 2px solid ${isRecommended ? '#3949AB' : '#e5e7eb'};
                            border-radius: 8px;
                            background: ${isRecommended ? '#f0f4ff' : !isMatching ? '#f5f5f5' : 'white'};
                            cursor: ${isMatching ? 'pointer' : 'not-allowed'};
                            transition: all 0.2s;
                            text-align: left;
                            opacity: ${isMatching ? 1 : 0.5};
                        "
                    >
                        <div style="display: flex; align-items: center; gap: 8px; width: 100%;">
                            <span style="font-size: 14px; font-weight: 500; color: ${isMatching ? '#1f2937' : '#9ca3af'};">
                                ${tour.name}
                            </span>
                            ${isRecommended ? '<span style="font-size: 10px; background: #3949AB; color: white; padding: 2px 6px; border-radius: 4px;">Khớp</span>' : ''}
                            ${!isMatching ? '<span style="font-size: 10px; background: #ef4444; color: white; padding: 2px 6px; border-radius: 4px;">Không khớp</span>' : ''}
                        </div>
                        <span style="font-size: 12px; color: #6b7280; margin-top: 2px;">
                            ${tour.description}
                        </span>
                    </button>
                `
                }).join('')}
            </div>
        `

        document.body.appendChild(overlay)
        document.body.appendChild(modal)

        // Add event listeners
        modal
            .querySelector('#tour-selector-close')
            ?.addEventListener('click', endTour)

        modal
            .querySelectorAll('.tour-option-btn:not([disabled])')
            .forEach((btn) => {
                btn.addEventListener('click', (e) => {
                    const tourId = (e.currentTarget as HTMLElement).dataset
                        .tourId
                    const tour = TOUR_OPTIONS.find((t) => t.id === tourId)
                    if (tour) {
                        startSelectedTour(tour)
                    }
                })

                // Hover effects - only for enabled buttons
                const isRecommended =
                    (btn as HTMLElement).style.borderColor ===
                    'rgb(57, 73, 171)'
                btn.addEventListener('mouseenter', (e) => {
                    const el = e.currentTarget as HTMLElement
                    if (!el.hasAttribute('disabled')) {
                        el.style.borderColor = '#3949AB'
                        el.style.background = '#f0f4ff'
                    }
                })
                btn.addEventListener('mouseleave', (e) => {
                    const el = e.currentTarget as HTMLElement
                    if (!el.hasAttribute('disabled') && !isRecommended) {
                        el.style.borderColor = '#e5e7eb'
                        el.style.background = 'white'
                    }
                })
            })

        setShowSelector(true)
    }, [endTour, startSelectedTour, getCurrentProductGroup])

    const startTour = useCallback(() => {
        showTourSelector()
    }, [showTourSelector])

    // Watch for pending tour - start when product is added
    useEffect(() => {
        if (!pendingTourRef.current || !formControl) return

        const tour = pendingTourRef.current
        const products = formControl.getValues('order.products') || []
        const hasMatchingProduct = products.some(
            (p) => p.product_group_name === tour.productGroup
        )

        if (hasMatchingProduct) {
            // Clear pending and start tour after render
            pendingTourRef.current = null

            // Inject dynamic config steps if applicable
            const finalTour = injectDynamicConfigSteps(tour)

            setSelectedTour(finalTour)
            setIsActive(true)
            setCurrentTourStep(0)

            // Wait for DOM to update then start tour
            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' })
                setTimeout(() => {
                    showTourUI(finalTour.steps[0], 0, finalTour)
                }, 300)
            }, 500)
        }
    })

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupTourElements()
            removeSelectorModal()
        }
    }, [])

    return {
        startTour,
        endTour,
        isActive,
        currentStep: currentTourStep,
        showSelector,
        selectedTour,
        tourOptions: TOUR_OPTIONS,
    }
}

export default useContractTour
